import React, { useEffect, useState, useMemo } from 'react';
// Library Imports
import axios from 'axios';
import moment from 'moment';
import { Button, Form } from '../ui-kit';
import Loader from '../general/Loader';

import { activityTypesFor, ErrorAlert, shouldSave, getWarningsFor } from './shared/utils';
import './shared/index.css';
import { redirectTo } from '../../utils/redirect';
import OverlappingAttendancesWarning from './OverlappingAttendancesWarning';

// This is the base class for all Activity Report forms (incidents and
// general_activity_reports)
//
// The two key components that need to be supplied to modify the behavior of
// this component are:
// - FieldsComponent: a component that renders the fields for editing the report
//   itself
// - AttendancesTableComponent: a component which shows the attendees of the
//   report with functionality to edit
const AbstractActivityReportForm = ({
  id,
  getActivityReport,
  createActivityReport,
  storeActivityReport,
  updateReportFields,
  createActivityReportAttendances,
  storeActivityReportAttendance,
  updateAttendanceFields,
  fetchActivityTypes,
  fetchUsers,
  fetchGroups,
  fetchTasks,
  fetchRanks,
  fetchAvailabilityCodes,
  fetchMemberships,
  fetchSkills,
  groupId, // ID of the station this report will be associated with
  userGroupScopeId,
  activityReports,
  activityReportAttendances,
  activityTypes,
  groups,
  tasksById,
  ranksById,
  availabilityCodes,
  availabilityCodesLoaded,
  selectAvailabilityCodeRights, // whether the user can assign a code
  overrideInitialLoadComplete, // for testing
  createNewAttendance, // function to create an empty new attendance
  FieldsComponent, // the component for the fields of the report
  AttendancesTableComponent, // the component for rendering attendees
  cancelPath, // function which returns the path when clicking cancel
  reportType,
  // IMPORTANT: The following props are only needed to comply with tests
  // These can be removed once the tests are updated to handle a mock store
  // Because the OverlappingAttendancesWarning component can be directly
  // connected to the store
  overlappingAttendancesByUserId,
  activityOverlappingAttendances,
  fetchIncidents,
  fetchActivities,
  readOnlyFields = [],
  startTime,
  endTime,
  createAttendancesForMembershipIds
}) => {
  useEffect(() => {
    fetchActivityTypes();
    fetchGroups({
      params: { group_ids: userGroupScopeId, in_hierarchy: true, presentation_type: 'basic', group_type: 'station' }
    });
    fetchUsers({
      params: { group_ids: userGroupScopeId, in_hierarchy: true, presentation_type: 'basic' }
    });
    fetchRanks();
    fetchAvailabilityCodes && fetchAvailabilityCodes({ params: { group_ids: groupId } });
    // can't use basic presentation_type for members, because we need skills
    fetchMemberships &&
      fetchMemberships({ params: { group_ids: userGroupScopeId, in_hierarchy: true, group_type: 'station' } });

    fetchSkills && fetchSkills({ params: { group_ids: groupId, in_hierarchy: true } });

    fetchTasks &&
      fetchTasks({
        params: { group_ids: groupId, in_hierarchy: true, presentation_type: 'basic' }
      });
  }, []);

  const [reportId, setReportId] = useState(id);
  const [isSaving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [initialAttendancesLoadComplete, setInitialAttendancesLoadComplete] = useState(
    overrideInitialLoadComplete || false
  );

  const loadReport = async () => {
    // If the form receives an ID, it means its an edit, and should load the
    // activity report's data. If not, create a new blank report
    if (id) {
      await getActivityReport(id);
    } else {
      const report = await createActivityReport({ group_id: groupId, start_time: startTime, end_time: endTime });
      setReportId(report.id);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const report = activityReports[reportId];

  // Attendances of the report are stored in the activityReportAttendances state
  // space. Get all attendances for the current report
  const attendances = activityReportAttendances.filter(
    r => r.activity_report_id == reportId || r.incident_id == reportId
  );

  const positiveAttendances = attendances.filter(a => ['dispatched', 'attended'].includes(a.reported_status));

  const attendeeIds = useMemo(
    () => positiveAttendances.map(a => a.user_id),
    // use a string join to avoid unnecessary re-renders due to array reference changes
    [positiveAttendances.map(a => a.user_id).join(',')]
  );

  // Create "unknown" attendances for all accessible users who don't have a
  // registered attendance
  const buildMissingAttendances = async () => {
    // this is for preventing API calls with wrong id that will cause an 404
    // error
    if (Object.keys(errors).length > 0 || !report || report.transient) return;
    let { start_time, end_time } = report;
    // incidents can have an empty end time
    end_time = end_time || moment(start_time).add(2, 'hours').format();

    const params = { start_time, end_time };
    const response = await axios.get(`/api/v2/activity_reports/${reportId}/candidates`, { params });

    // remove all memberships that are already an attendee for this activity
    const userIds = new Set(attendances.map(attendance => attendance.user_id));
    const memberships = response.data.filter(membership => !userIds.has(membership.user_id));

    createAttendancesForMembershipIds = createAttendancesForMembershipIds || [];
    const newAttendances = memberships.map(membership =>
      createNewAttendance(
        membership,
        reportId,
        createAttendancesForMembershipIds.includes(membership.id) ? 'attended' : 'unknown'
      )
    );

    const createdAttendances = await createActivityReportAttendances(newAttendances);

    for (const attendance of createdAttendances.filter(a => a.reported_status != 'unknown')) {
      storeActivityReportAttendance(attendance.id);
    }

    // If this is the first time building attendances, we will indicate that
    // the initial loading of attendances is complete. The attendances table
    // will listen to this value to determine whether to show a loader or the
    // table. This is necessary to show the table until after the attendances
    // have been properly loaded, which avoids issues with the auto-scaling of
    // the table
    setInitialAttendancesLoadComplete(true);
  };

  useEffect(() => {
    buildMissingAttendances();
  }, [report?.id, report?.start_time, report?.end_time]);

  // List of activity types that are accessible through the current group
  const eligibleActivityTypes = activityTypesFor({
    activityTypes,
    groupId,
    groupsById: groups.byId,
    appliesTo: reportType
  });

  const shouldRenderAttendances = !report?.transient && groups.lastFetch && initialAttendancesLoadComplete;

  const saveActivityReport = async () => {
    const warnings = getWarningsFor(report, true);
    if (!shouldSave(warnings)) return;
    setSaving(true);
    setErrors({});
    // When saving for the first time, the report transitions from transient to
    // persisted with this we can indicate the page to redirect only if the
    // report is already persisted
    const firstSave = report.transient;

    // Remove incident responses from the payload to avoid overwriting changes made
    // for individual attendances.
    const action = await storeActivityReport(reportId, { modifications: { incident_responses: null } });
    const { data, validationErrors } = action.payload;
    // when saving a new activity report, the ID will change from a temporary
    // ID to the ID in the backend. Capture this, then redirect to the 'old'
    // html page to view the report;
    // data can be null, therefore we need to check that data exists before getting id
    const id = data ? data.id : reportId;
    setSaving(false);
    setReportId(id);

    setErrors(validationErrors || {});

    const anyErrors = Object.keys(validationErrors || {}).length > 0;

    if (!firstSave && !anyErrors) {
      redirectTo(`/activity_reports/${reportId}`);
    }
  };

  const saveAttendance = (id, values) => {
    updateAttendanceFields(id, values);
    storeActivityReportAttendance(id);
  };

  const updateFields = values => {
    // store field update in Redux directly
    updateReportFields(reportId, values);
  };

  const updateAvailabilityCode = values => {
    updateFields({ availability_code_id: values[0] || null });
  };

  const updateDatetimeFields = values => {
    updateFields(values);
  };

  if (!report) return <Loader />;

  return (
    <Form>
      <ErrorAlert errors={errors} />
      <FieldsComponent
        report={report}
        updateFields={updateFields}
        eligibleActivityTypes={eligibleActivityTypes}
        tasksById={tasksById}
        updateDatetimeFields={updateDatetimeFields}
        selectAvailabilityCodeRights={selectAvailabilityCodeRights}
        availabilityCodes={availabilityCodes}
        availabilityCodesLoaded={availabilityCodesLoaded}
        updateAvailabilityCode={updateAvailabilityCode}
        readOnlyFields={readOnlyFields}
      />
      <div className="form-actions my-3 text-end">
        <Button href={cancelPath(groupId, reportId)} variant="secondary" disabled={isSaving} className="me-2">
          {I18n.t('cancel')}
        </Button>
        <Button variant="success" onClick={saveActivityReport} disabled={isSaving}>
          {I18n.t(report.transient ? 'create' : 'save')}
        </Button>
      </div>
      {report.transient || (
        <OverlappingAttendancesWarning
          startTime={report.start_time}
          endTime={report.end_time}
          activity={report}
          userIds={attendeeIds}
          activityOverlappingAttendances={activityOverlappingAttendances}
          fetchIncidents={fetchIncidents}
          fetchActivities={fetchActivities}
          groupIds={groupId}
        />
      )}
      {shouldRenderAttendances ? (
        <AttendancesTableComponent
          ranks={ranksById}
          attendances={attendances}
          saveAttendance={saveAttendance}
          currentGroupId={groupId}
          currentReportStartTime={report.start_time}
          currentReportEndTime={report.end_time}
          overlappingAttendancesByUserId={overlappingAttendancesByUserId}
        />
      ) : (
        report.transient || <Loader />
      )}
    </Form>
  );
};

export default AbstractActivityReportForm;
